/* Qué clase de acoplado es: playo, furgón o portacontenedor.
 *
 * Lo pidió Ricardo cargando movimientos. Le hace falta para lo que ya venía
 * haciendo a mano en su planilla: pedir «6 piso liso» o «2 portacontenedor»
 * para el día siguiente, y saber si el acoplado que va a poner sirve para esa
 * carga. Hoy eso lo tiene en la cabeza.
 *
 * ── NACE VACÍA, Y NO ES UN OLVIDO ────────────────────────────────────────
 *
 * Se buscó el dato antes de crear la columna y no está en ninguna parte:
 *
 *   · `marca`   es el FABRICANTE —PINCEN, HERMANN, SCANIA, RANDON— y 616 de
 *               679 están vacías;
 *   · `modelo`  es el AÑO;
 *   · `detalle` tiene SIETE filas cargadas en todo el padrón;
 *   · `rto.tipo` es el tipo de REVISIÓN, no la carrocería.
 *
 * Son **679 de 679 sin cargar**, exactamente como nació `lona`. Hay que
 * pedirle la lista a quien la tenga.
 *
 * ── POR ESO SON CUATRO ESTADOS Y NO TRES ─────────────────────────────────
 *
 * `sin_dato` es el cuarto y el primer día es el ÚNICO. Dibujado como una
 * carrocería más —o peor, como «furgón», que es el nombre de la tabla— la
 * lista de portacontenedores arrancaría vacía y la de furgones con la flota
 * entera adentro, sin que nadie pueda ver que nadie lo cargó. Es la misma
 * regla que la lona, que el psicofísico sin fecha y que las 86 unidades que no
 * reportaron urea: **«no sabemos» no se dice como una respuesta.**
 *
 * ── EL VOCABULARIO LO FIJA LA BASE ───────────────────────────────────────
 *
 * Las claves están en el `check` de `furgones.tipo_acoplado`, y ése es el
 * único lugar donde el conjunto obliga a los dos lados: el hub lo edita y
 * FlotaBot lo muestra al cargar un viaje. Las etiquetas visibles sí las tiene
 * cada uno — que allá diga «Playo» y acá «Playo» no es algo que se pueda
 * romper en silencio, y el conjunto de claves sí.
 */

export const PLAYO = "playo";
export const FURGON = "furgon";
export const PORTACONTENEDOR = "portacontenedor";
export const SEMI = "semi";
export const VUELCO = "vuelco";
export const SR_FD_CG = "sr_fd_cg";
export const SIN_DATO = "sin_dato";

/* El orden en el que se ofrecen. `sin_dato` NO está: no es algo que alguien
 * elija, es el estado en el que están las 679. Se muestra, no se ofrece —
 * misma decisión que en la lona.
 *
 * `sr_fd_cg` TAMPOCO se ofrece, y por otro motivo: es una sola unidad y no
 * sabemos qué quiere decir la sigla. Ofrecerla en el desplegable invitaría a
 * clasificar otras ahí sin que nadie sepa qué está eligiendo. Se muestra —ver
 * `TODOS`— y se pregunta. */
export const TIPOS = [
  { clave: PLAYO, etiqueta: "Playo", corto: "Playo" },
  { clave: FURGON, etiqueta: "Furgón", corto: "Furgón" },
  { clave: PORTACONTENEDOR, etiqueta: "Portacontenedor", corto: "Portacont." },
  { clave: SEMI, etiqueta: "Semi", corto: "Semi" },
  { clave: VUELCO, etiqueta: "Vuelco trasero", corto: "Vuelco" },
];

/* Los que la pantalla sabe DIBUJAR, que no son los mismos que sabe ofrecer.
 *
 * La diferencia existe por `sr_fd_cg`: si no estuviera acá, `etiquetaTipo`
 * caería en «Sin dato» sobre una unidad que SÍ está clasificada, y el síntoma
 * mandaría a cargar de nuevo algo que ya estaba cargado. `aConfirmar` es lo
 * que hace que se vea distinto: está, pero nadie confirmó qué es. */
export const TODOS = [
  ...TIPOS,
  {
    clave: SR_FD_CG,
    etiqueta: "SR-FD-CG",
    corto: "SR-FD-CG",
    aConfirmar: true,
  },
];

/* Si la carrocería está a confirmar. La pantalla que lo dibuje debería
 * marcarlo —un ⚠️, o la etiqueta en gris— por el mismo motivo que el CRT marca
 * lo que leyó dudoso: un dato sin confirmar mostrado igual que uno confirmado
 * se vuelve confirmado por costumbre. */
export function aConfirmar(valor) {
  const t = TODOS.find((x) => x.clave === tipoDe(valor));
  return Boolean(t && t.aConfirmar);
}

/* Cómo lo escribe la gente → la clave. Es un vocabulario CERRADO y confirmado
 * —son tres carrocerías— así que acá unificar es traducir y no inventar. Es la
 * misma distinción que separa a `empresa.js` (glosario cerrado, se unifica) de
 * los clientes de `movimientos` (256 nombres abiertos, no se tocan).
 *
 * «Piso liso» entra porque es como lo llama Ricardo en su planilla: 216 filas
 * lo piden así y ninguna dice «playo». Si el hub sólo entendiera «playo», el
 * día que se importe algo de esa planilla no matchearía nada. */
const ALIAS = {
  playo: PLAYO,
  "piso liso": PLAYO,
  piso: PLAYO,
  "playo baranda volcable": PLAYO,
  furgon: FURGON,
  "furgón": FURGON,
  portacontenedor: PORTACONTENEDOR,
  portacont: PORTACONTENEDOR,
  "porta contenedor": PORTACONTENEDOR,
  contenedor: PORTACONTENEDOR,
  /* Como lo escribe Aldo en CONTROL CARPETA DE FURGONES 2025. Las dos mitades
   * del bitrén son el mismo semi: la D (delantero) y la T (trasero) dicen
   * dónde va en el tren, no qué caja es. */
  semi: SEMI,
  "bitren-d-semi": SEMI,
  "bitren-t-semi": SEMI,
  vuelco: VUELCO,
  "vuelco trasero": VUELCO,
  /* Las DOS formas: con guión medio es como la escribe la planilla, con guión
   * bajo es la clave que guarda la base. Sin la segunda, `tipoDe` sobre una
   * fila ya clasificada devolvía «sin dato» — que es justo el síntoma que este
   * archivo dice evitar, y que encontró el test. */
  "sr-fd-cg": SR_FD_CG,
  sr_fd_cg: SR_FD_CG,
};

const plano = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/* De lo que hay en la fila al tipo. Devuelve `sin_dato` para el hueco Y para
 * lo que no se reconoce: las dos cosas son «esta fila no dice qué es», y
 * adivinar sobre un valor raro es peor que mostrarlo sin cargar. */
export function tipoDe(valor) {
  const v = plano(valor);
  if (!v) return SIN_DATO;
  return ALIAS[v] || SIN_DATO;
}

/* Cómo se dice. Sin dato dice «Sin dato», nunca una carrocería.
 *
 * Busca en `TODOS` y no en `TIPOS`: lo que se puede DIBUJAR es más que lo que
 * se puede ELEGIR. Con `TIPOS`, la unidad SR-FD-CG diría «Sin dato» estando
 * clasificada, y eso manda a cargar de nuevo algo que ya está cargado. */
export function etiquetaTipo(valor) {
  const t = TODOS.find((x) => x.clave === tipoDe(valor));
  return t ? t.etiqueta : "Sin dato";
}

/* Cuántos hay de cada uno, con los que faltan PRIMERO.
 *
 * El número de sin cargar va a la vista porque es lo único que hace que la
 * columna se llene alguna vez: «12 portacontenedores» sobre 679 se lee como un
 * inventario terminado. Misma regla que la lona y que el tipo de servicio. */
export function contarTipos(filas) {
  const out = { [SIN_DATO]: 0 };
  for (const t of TIPOS) out[t.clave] = 0;
  for (const f of filas || []) out[tipoDe(f?.tipo_acoplado)] += 1;
  return out;
}

/* Lo que hay escrito en la columna y el vocabulario no reconoce.
 *
 * NO se esconde: una carrocería mal tipeada deja a ese acoplado fuera de todas
 * las listas **sin fallar en ningún lado**, y el síntoma —«no me aparece»— no
 * señala a esta lista. Es la idea de `zona.desconocidas()` y de
 * `permisos_desconocidos()`. El `check` de la base hace que esto tenga que
 * estar vacío; si algún día no lo está, es que alguien escribió por afuera. */
export function desconocidos(filas) {
  const cuenta = new Map();
  for (const f of filas || []) {
    const crudo = String(f?.tipo_acoplado ?? "").trim();
    if (!crudo || tipoDe(crudo) !== SIN_DATO) continue;
    cuenta.set(crudo, (cuenta.get(crudo) || 0) + 1);
  }
  return [...cuenta.entries()]
    .map(([valor, acoplados]) => ({ valor, acoplados }))
    .sort((a, b) => b.acoplados - a.acoplados);
}
